// Data hook of the global search: one request per settled query (GET /orgs/{orgId}/search, members,
// 60 requests a minute). Typing is debounced, a query under two characters is never sent, an
// identical query is served from the cache for a minute, and the previous results stay on screen
// while the next ones load, so the list does not flash.
import { useSearchControllerFind } from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import {
  DEBOUNCE_MS,
  MIN_QUERY_LENGTH,
  buildListModel,
  type SearchListModel,
  type SearchScope,
} from './model';

export type SearchViewState = 'prompt' | 'tooShort' | 'loading' | 'error' | 'empty' | 'results';

export interface SearchView {
  /** The settled (debounced) query the results belong to. */
  q: string;
  state: SearchViewState;
  /** The error was a 429: the person searched too often. */
  rateLimited: boolean;
  list: SearchListModel;
  /** A request is running or the typing has not settled yet. */
  busy: boolean;
  /** More results exist for a kind than were returned: refine the search. */
  hasMore: boolean;
  retry: () => void;
}

export interface UseSearchViewOptions {
  orgId: string;
  /** What is typed. */
  input: string;
  scope: SearchScope;
  limit: number;
  idPrefix: string;
  perGroup: number;
  seeMore: boolean;
}

export function useSearchView({
  orgId,
  input,
  scope,
  limit,
  idPrefix,
  perGroup,
  seeMore,
}: UseSearchViewOptions): SearchView {
  const typed = input.trim();
  const q = useDebouncedValue(typed, DEBOUNCE_MS);
  const enabled = typed.length >= MIN_QUERY_LENGTH && q.length >= MIN_QUERY_LENGTH;
  const query = useSearchControllerFind(
    orgId,
    { q, scope, limit },
    {
      query: {
        enabled,
        staleTime: 60_000,
        retry: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
      },
    },
  );

  const groups = query.data?.groups;
  const list = useMemo(
    () => buildListModel(groups ?? [], { orgId, q, idPrefix, perGroup, seeMore }),
    [groups, orgId, q, idPrefix, perGroup, seeMore],
  );

  const settled = typed === q;
  let state: SearchViewState;
  if (typed.length === 0) state = 'prompt';
  else if (typed.length < MIN_QUERY_LENGTH) state = 'tooShort';
  else if (query.isError && settled) state = 'error';
  else if (!query.data || !settled || query.isFetching)
    state = list.count > 0 ? 'results' : 'loading';
  else state = list.count > 0 ? 'results' : 'empty';

  return {
    q,
    state,
    rateLimited: query.error instanceof ApiError && query.error.status === 429,
    list: enabled ? list : buildListModel([], { orgId, q, idPrefix, perGroup, seeMore }),
    busy: enabled && (!settled || query.isFetching),
    hasMore: Boolean(groups?.some((g) => g.hasMore)),
    retry: () => {
      void query.refetch();
    },
  };
}
